import { ToggleGroup } from '@digdir/designsystemet-react'
import { Icon, type IconName } from './Icon'

export interface ViewSwitchOption<V extends string = string> {
  /** Stable value emitted by onChange when this option is picked. */
  value: V
  /** Visible label (already i18n'd by the caller). */
  label: string
  /** Optional leading icon from the shared Icon registry. */
  icon?: IconName
}

export interface ViewSwitchProps<V extends string = string> {
  options: ViewSwitchOption<V>[]
  /** Currently selected value. */
  value: V
  /** Notified with the option value when a different segment is activated. */
  onChange: (value: V) => void
  /** Accessible label for the whole control (already i18n'd). */
  ariaLabel?: string
  ariaDescribedBy?: string
}

/**
 * Segmented single-select control.
 *
 * Usage:
 *   <ViewSwitch
 *     ariaLabel={t('admin.cases.views.label')}
 *     options={[
 *       { value: 'table', label: t('admin.cases.views.table'), icon: 'list' },
 *       { value: 'kanban', label: t('admin.cases.views.kanban'), icon: 'flow' },
 *       { value: 'cards', label: t('admin.cases.views.cards'), icon: 'grid' },
 *     ]}
 *     value={view}
 *     onChange={setView}
 *   />
 */
export function ViewSwitch<V extends string = string>({
  options,
  value,
  onChange,
  ariaLabel = '',
  ariaDescribedBy,
}: ViewSwitchProps<V>) {
  return (
    <ToggleGroup
      className="disp-seg"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      value={value}
      onChange={(next) => { if (next !== value) onChange(next as V) }}
      onFocusCapture={(event) => event.target.closest('label')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })}
      // DS focusgroup suppresses native arrow selection; keep native radio behavior.
      {...{ focusgroup: 'none' }}
    >
      {options.map((option) => {
        const isOn = option.value === value
        return (
          <ToggleGroup.Item
            key={option.value}
            value={option.value}
            className={`disp-seg__b${isOn ? ' is-on' : ''}`}
          >
            {option.icon && <Icon name={option.icon} size={14} aria-hidden="true" />}
            <span>{option.label}</span>
          </ToggleGroup.Item>
        )
      })}
    </ToggleGroup>
  )
}
